import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useRef, Fragment } from "react";
import { Phone, Zap, PenLine, CalendarCheck, Star, Megaphone, Clock, Check } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { CtaButton } from "@/components/site/CtaButton";
import { Marquee } from "@/components/site/Marquee";
import { BeforeAfterSlider } from "@/components/site/BeforeAfterSlider";
import { CapacityCalculator } from "@/components/site/CapacityCalculator";
import { Accordion } from "@/components/site/Accordion";
import { Seo } from "@/components/Seo";
import { IMAGES } from "@/lib/assets";
import { EASE, maskLine } from "@/lib/anim";

const Eyebrow = ({ children, tone = "accent" }) => (
  <span className={tone === "accent" ? "eyebrow" : "eyebrow-muted"}>{children}</span>
);

const HERO_LINES = [
  [{ t: "GET YOUR", a: false }],
  [{ t: "BUSINESS", a: false }],
  [{ t: "CHECKED.", a: true }],
];

const Hero = () => {
  const reduce = useReducedMotion();
  return (
    <section className="relative min-h-screen flex flex-col justify-center pt-28 pb-16 overflow-hidden" data-testid="hero">
      <div className="shell grid lg:grid-cols-[1.05fr_0.95fr] gap-16 items-center">
        <div>
          <h1 className="display mt-8 text-[#f5f3ee]" style={{ fontSize: "clamp(2.7rem, 7vw, 6.5rem)" }}>
            {HERO_LINES.map((line, li) => (
              <span className="line-mask" key={li}>
                <motion.span
                  className="block"
                  variants={maskLine}
                  initial={reduce ? "show" : "hidden"}
                  animate="show"
                  transition={{ duration: 1.05, ease: EASE, delay: 0.15 + li * 0.12 }}
                >
                  {line.map((seg, si) => (
                    <span key={si} className={seg.a ? "text-[#5CCBF5]" : ""}>
                      {seg.t}
                    </span>
                  ))}
                </motion.span>
              </span>
            ))}
          </h1>

          <Reveal delay={0.6} className="mt-8 max-w-xl">
            <p className="display text-xl sm:text-2xl text-[#f5f3ee] leading-snug">
              One free examination. A personalized diagnosis. <span className="text-[#5CCBF5]">A clear prescription.</span>
            </p>
          </Reveal>

          <Reveal delay={0.7} className="mt-6 max-w-xl">
            <p className="text-[#a7a7a2] text-lg sm:text-xl leading-relaxed">
              AI Doctor examines your business, finds where you're losing time, money and capacity, and prescribes
              the AI treatments that can actually make a difference.
            </p>
          </Reveal>

          <Reveal delay={0.85} className="mt-10 flex flex-wrap items-center gap-4">
            <CtaButton to="/apply" testid="hero-primary-cta">Get Your Free AI Check-Up</CtaButton>
          </Reveal>
          <Reveal delay={0.92}>
            <p className="mt-4 font-mono text-[11px] tracking-[0.14em] uppercase text-[#5c5c58]">
              Typically 30–45 minutes. Free. No card required.
            </p>
          </Reveal>

          <Reveal delay={1} className="mt-8">
            <p className="font-mono text-[11px] tracking-[0.14em] uppercase text-[#5CCBF5]">
              Your AI Doctor adapts the examination to your business.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.4} className="relative">
          <div className="relative border border-white/10 overflow-hidden bg-[#131C31]" data-testid="founder-portrait">
            <img
              src={IMAGES.founderPortrait}
              alt="Nicolas Moreau, founder of The AI Doctor"
              className="w-full h-auto object-cover aspect-[3/4]"
            />
            <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8 bg-gradient-to-t from-[#0E1526] via-[#0E1526]/70 to-transparent">
              <p className="display text-2xl sm:text-3xl text-[#f5f3ee]">Nicolas Moreau</p>
              <p className="font-mono text-[11px] tracking-[0.24em] uppercase text-[#5CCBF5] mt-2">
                The AI Doctor / Founder
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const SectionBg = ({ src, opacity = "opacity-[0.72]", overlay = "bg-[#0E1526]/20" }) => (
  <div className="absolute inset-0 -z-10 pointer-events-none" aria-hidden="true">
    <img src={src} alt="" className={`h-full w-full object-cover object-center ${opacity}`} />
    <div className={`absolute inset-0 ${overlay}`} />
  </div>
);

const SectionHead = ({ eyebrow, title, tone = "accent", className = "", children }) => (
  <div className={className}>
    <Reveal><Eyebrow tone={tone}>{eyebrow}</Eyebrow></Reveal>
    <Reveal delay={0.08}>
      <h2 className="display mt-6 text-[#f5f3ee] text-4xl sm:text-5xl lg:text-6xl max-w-4xl">{title}</h2>
    </Reveal>
    {children}
  </div>
);

const BOTTLENECKS = [
  ["01", "THE INBOX", "You answer the same 5 questions every single day, and Ctrl+C / Ctrl+V is basically your co-founder."],
  ["02", "THE NO-SHOWS", "Clients forget appointments because your “reminder system” is currently you, texting them at 11pm."],
  ["03", "THE CONTENT TREADMILL", "You know you should post more. You have not posted since Q1."],
  ["04", "THE SOLO ACT", "If you get sick for a week, so does your business."],
  ["05", "THE SPREADSHEET GRAVEYARD", "You have 14 tools. You remember the passwords to 3 of them."],
  ["06", "THE “I'LL LOOK INTO AI” LOOP", "You've bookmarked 40 AI tools and used exactly zero of them."],
];

const CLASSIFY = [
  ["SKIP IT", "Does this task even need to happen?"],
  ["AUTOMATE IT", "Can software handle this end-to-end while you sleep?"],
  ["ASSIST IT", "Can AI do the first draft so you just hit approve?"],
  ["HAND IT OFF", "Can someone — or something — else just own this?"],
  ["KEEP IT", "Some things only you should do. I protect those too."],
];

const BUILDS = [
  {
    id: "rx-01",
    code: "Rx-01 · Primary Diagnosis",
    title: "CUSTOMER COMMUNICATION",
    desc: "Answering, booking, reminding, following up — without you doing it by hand.",
    ingredient: "Autonomous 24/7 Response Engine",
    efficacy: "15–22 hrs/wk recovered",
    isHero: true,
    bullets: ["Multichannel Lead Response", "Automated Booking & Calendar Sync", "No-Show Prevention Text Sequences"],
  },
  {
    id: "rx-02",
    code: "Rx-02",
    title: "CONTENT & MARKETING",
    desc: "Getting your message out consistently, without hiring a whole team.",
    ingredient: "Synthetic Content Pipeline",
    efficacy: "5x publishing frequency",
    isHero: false,
  },
  {
    id: "rx-03",
    code: "Rx-03",
    title: "ADMIN & OPERATIONS",
    desc: "Invoicing, scheduling, reporting — quietly handled in the background.",
    ingredient: "Silent Process Automation",
    efficacy: "Zero manual entry",
    isHero: false,
  },
  {
    id: "rx-04",
    code: "Rx-04",
    title: "LEAD FOLLOW-UP",
    desc: "No lead falls through the cracks because you forgot to reply.",
    ingredient: "Zero-Latency Lead Recovery",
    efficacy: "Every lead answered",
    isHero: false,
  },
  {
    id: "rx-05",
    code: "Rx-05",
    title: "KNOWLEDGE YOU DON'T HAVE TO REMEMBER",
    desc: "Your prices, policies and FAQs — answered instantly, correctly, every time.",
    ingredient: "Internal Knowledge Memory",
    efficacy: "100% policy accuracy",
    isHero: false,
  },
  {
    id: "rx-06",
    code: "Rx-06",
    title: "LEVERAGE FOR TEAMS OF ONE",
    desc: "Even a solo operation gets the output of a five-person team.",
    ingredient: "Multi-Agent Force Multiplier",
    efficacy: "5-person output",
    isHero: false,
  },
];

const WECHECK = [
  ["REVENUE", "Money recovered or generated."],
  ["TIME", "Hours returned to you and your team."],
  ["COST", "Unnecessary labor and process costs cut."],
  ["CAPACITY", "Extra business you can actually handle."],
  ["CUSTOMER EXPERIENCE", "Faster answers, fewer dropped balls."],
  ["OPERATIONS", "Less admin, cleaner workflows."],
  ["KNOWLEDGE", "Your information, findable and usable."],
  ["AI READINESS", "What you already have — and actually use."],
];

const SYSTEMS = [
  {
    icon: Phone,
    name: "Missed-Call Text-Back",
    time: "Installed in 48 hours",
    fixes: "Every missed call gets a text within 30 seconds with a link to book — so the caller books with you, not your competitor.",
    number: "+$3,600/mo",
    caption: "Turn just 3 missed calls a week into bookings at a $300 average job.",
    source: "Example",
    bullets: ["Works with your current number", "Custom message in your brand voice", "Every lead logged automatically"],
  },
  {
    icon: Zap,
    name: "Speed-to-Lead",
    time: "Installed in 3 days",
    fixes: "Every form on your website gets a personal reply in under 5 minutes, 24/7 — and the lead is pushed to book.",
    number: "7× more qualified",
    caption: "Leads contacted within the hour are nearly 7× more likely to qualify.",
    source: "Harvard Business Review, 2011",
    bullets: ["Instant email + text reply", "Answers common questions", "Books straight into your calendar"],
  },
  {
    icon: PenLine,
    name: "Quote Chaser",
    time: "Installed in 3 days",
    fixes: "Every open quote is followed up automatically on day 2, 5 and 10 — until you get a yes or a no.",
    number: "+$3,000/mo",
    caption: "Close just 2 more quotes a month at $1,500 each.",
    source: "Example",
    bullets: ["Friendly, non-pushy follow-ups", "Stops the moment they reply", "Alerts you when a client is ready"],
  },
  {
    icon: CalendarCheck,
    name: "No-Show Shield",
    time: "Installed in 48 hours",
    fixes: "Every client gets text and email reminders before their appointment, can confirm or reschedule in one tap, and cancelled slots get refilled.",
    number: "+$2,000/mo",
    caption: "Prevent just 4 no-shows a month at $500 per appointment.",
    source: "Example",
    bullets: ["Automatic reminders before every appointment", "One-tap confirm or reschedule", "Cancelled slots offered to your waitlist"],
  },
  {
    icon: Star,
    name: "Review Engine",
    time: "Installed in 48 hours",
    fixes: "After every job, customers get a simple request to leave a Google review — and unhappy feedback comes to you first.",
    number: "+13 reviews/mo",
    caption: "20 jobs a month × 65% of customers who leave a review when asked.",
    source: "Example · 65% from BrightLocal",
    bullets: ["Sent by text and email", "One-tap link to your Google profile", "Alerts you to unhappy customers"],
  },
  {
    icon: Megaphone,
    name: "Past-Customer Reactivation",
    time: "Live in 7 days",
    fixes: "A campaign to the customers you already have, with an offer that brings them back — no new ad spend.",
    number: "+$2,500/mo",
    caption: "Win back just 5 past customers a month at $500 each.",
    source: "Example",
    bullets: ["Uses your existing customer list", "Written and sent for you", "Replies handled and booked"],
  },
];

const PROCESS = [
  ["01", "CHECK-UP", "Free · 30–45 min", "I examine your business. Not the highlight reel — the real thing: how work actually moves through your day, where replies pile up, which tasks you copy-paste for the fortieth time, which tools you pay for but never open. The Check-Up adapts to your answers as you go: I only ask what can materially change the diagnosis."],
  ["02", "DIAGNOSIS", "Yours to keep", "I show you what's wrong and where the opportunity is. You receive a personalized AI Business Diagnosis: the specific places you're losing time, money, leads or capacity — ranked by impact, each with an estimated annual value in conservative, expected and upside scenarios, with a confidence level attached. Ranges, not promises. And if your business is already running clean, I tell you that too."],
  ["03", "PRESCRIPTION", "Plain English · Ranked", "I tell you what I recommend — and why. A short, ranked list of exactly what to do next, written in plain English, with the reasoning behind every line. At least 5 fixes immediately actionable, free, with step-by-step instructions. The prescription is yours to keep, whether or not you ever buy anything from me."],
  ["04", "THE CALL", "Free · Direct access", "You speak directly with the Doctor. A free walkthrough of your diagnosis: your health score, your biggest opportunities, the numbers behind them, and the 3–5 treatments I believe would make the biggest difference. No generic AI pitch, no sales presentation — just your business, the findings, and what I'd actually do about them."],
  ["05", "TREATMENT", "Optional", "I implement what makes sense. Only what the diagnosis justifies — scoped from the treatment library, quoted only after your diagnosis, never before. Some businesses need a single quick fix, some need a connected system, some need nothing built at all. The treatment depends on the diagnosis — not the other way around."],
  ["06", "AI CARE", "Ongoing", "I keep it running, improving and evolving. Monitoring, optimization, new automations, reporting and regular system health checks. Your business changes — new services, new staff, new bottlenecks — and your AI is adjusted to keep up, so the results don't quietly decay six months later."],
];

const DIAGNOSIS_FINDINGS = [
  ["01", "Missed Lead Recovery", "€38K–€61K / year", "High"],
  ["02", "Quote Follow-Up", "€24K–€41K / year", "Medium"],
  ["03", "Administrative Automation", "420–680 hours / year", "High"],
  ["04", "Customer Reactivation", "€18K–€32K / year", "Medium"],
  ["05", "Review Generation & Reputation", "€7K–€12K / year", "Medium"],
  ["06", "No-Show Reduction", "€6K–€11K / year", "High"],
  ["07", "Invoice & Payment Follow-Up", "90–140 hours / year", "High"],
  ["08", "Internal Knowledge Access", "60–110 hours / year", "Medium"],
  ["09", "Content & Marketing Consistency", "80–130 hours / year", "Low"],
];

const FAQ = [
  { q: "Is this an AI automation agency?", a: "No. AI Doctor is a doctor for your business: I examine first, prescribe second. If AI isn't the right answer, I say so — and if a tool you already pay for solves the problem, I won't prescribe new software." },
  { q: "How much does the Check-Up cost?", a: "Nothing. The AI Business Check-Up is free — a real examination of your business, not a disguised sales call. If you later want me to implement treatments, those are scoped and priced separately, only if you ask." },
  { q: "How long does the Check-Up take?", a: "Typically 30–45 minutes. It adapts to your business — I only ask questions that can materially change the diagnosis, the ROI or the treatment." },
  { q: "What do I actually walk away with?", a: "A personalized diagnosis — where you're losing time, money and capacity, with estimated annual impact — plus a written prescription of ranked fixes, at least 5 of them immediately actionable and free." },
  { q: "What if you don't find anything worth fixing?", a: "Then I tell you — that's a valid diagnosis too. You leave knowing your business is running clean, and it cost you nothing." },
  { q: "Will you replace my team?", a: "No. I free your team (or you) from the boring stuff — not from having a job." },
  { q: "Do I need to already understand AI?", a: "No. That's the whole point. You bring the business, I bring the diagnosis." },
  { q: "Do I need a big budget or fancy software?", a: "No — most prescriptions start with tools you already have or free tiers you can switch on this week." },
  { q: "What happens after the Check-Up?", a: "You can implement the prescription yourself, or I can build the treatments for you. Your call, no pressure. And if you want ongoing monitoring and optimization, that's what AI Care is for." },
  { q: "Do I have to buy implementation too?", a: "No. The diagnosis and the prescription are yours to keep either way. Treatment is a separate, optional conversation with its own scope and price." },
];

export default function Home() {
  const ctaRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: ctaRef, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <div data-testid="home-page">
      <Seo
        title="The AI Doctor — Get Your Free AI Check-Up"
        description="AI Doctor examines your business, finds where you're losing time, money and capacity, and prescribes the AI treatments that make a difference. Free Check-Up."
      />
      <Hero />

      <Marquee items={["Diagnosis First", "Walk-Ins Welcome", "No Jargon", "A Second Opinion", "Prescription Ready", "Fewer Tools, More Leverage", "Keep Your Evenings"]} />

      {/* FOUNDER QUOTE */}
      <section className="border-b border-white/10">
        <div className="shell py-24 lg:py-36">
          <Reveal>
            <p className="display text-2xl sm:text-3xl lg:text-4xl text-[#f5f3ee] leading-[1.25] max-w-4xl">
              <span className="text-[#5CCBF5]">"</span>AI created a thousand solutions and one massive problem: nobody knows which ones are actually worth using. Businesses are overwhelmed, losing time, and leaving opportunities on the table. Everyone knows AI could help. Almost nobody knows where to start, what to fix, or what will actually make a difference. That's why I built The AI Doctor. I examine your business, identify where you're losing time, money, and capacity, and prescribe the AI treatments that make the most sense for you. One free check-up. A personalized diagnosis. A clear prescription.<span className="text-[#5CCBF5]">"</span>
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 font-mono text-[11px] tracking-[0.24em] uppercase text-[#a7a7a2]">— Nicolas Moreau, Founder</p>
          </Reveal>
        </div>
      </section>

      {/* THE PROBLEM */}
      <section id="symptoms" className="relative overflow-hidden shell py-28 lg:py-40 scroll-mt-24 border-t border-white/10">
        <SectionBg src="/images/exam-room.png" />
        <SectionHead
          eyebrow="The Problem"
          title={<>Everyone knows they should be using AI. <span className="text-[#a7a7a2]">Almost nobody knows where.</span></>}
        />
        <div className="mt-14 grid lg:grid-cols-2 gap-14 lg:gap-24">
          <Reveal delay={0.1}>
            <p className="text-[#f5f3ee] text-lg sm:text-xl leading-relaxed">
              What should we automate? What should we keep human? Which tools? What are we already paying for but
              not using? Where are we actually losing money?
            </p>
            <p className="text-[#f5f3ee] text-lg sm:text-xl leading-relaxed mt-6">
              You didn't sign up to be the reminder system, the copywriter and the help desk. Yet here we are.
            </p>
          </Reveal>
          <Reveal delay={0.2} className="flex items-center">
            <p className="display text-2xl sm:text-3xl lg:text-4xl text-[#f5f3ee] leading-tight">
              AI Doctor finds the answer. Most of these have the same root cause:
              <span className="text-[#5CCBF5]"> you are the automation.</span>
            </p>
          </Reveal>
        </div>

        {/* Bottleneck grid */}
        <div className="mt-20 grid sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-white/10">
          {BOTTLENECKS.map(([n, t, d], i) => (
            <Reveal key={t} delay={i * 0.05} className="group border-r border-b border-white/10 p-8 lg:p-10 hover:bg-[#131C31] transition-colors duration-500">
              <span className="font-mono text-[11px] text-[#5c5c58]">{n}</span>
              <h3 className="display text-2xl text-[#f5f3ee] mt-5 group-hover:text-[#5CCBF5] transition-colors duration-300">{t}</h3>
              <p className="text-[#f5f3ee] mt-3 leading-relaxed">{d}</p>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1} className="mt-12 max-w-4xl">
          <p className="display text-2xl sm:text-3xl lg:text-4xl text-[#f5f3ee] leading-tight">
            You're not overworked. You're over-involved.
            <span className="text-[#5CCBF5]"> And unlike overwork, that's fixable.</span>
          </p>
        </Reveal>
      </section>

      {/* THE PROCESS */}
      <section id="process" className="shell py-28 lg:py-40 scroll-mt-24 border-t border-white/10">
        <SectionHead
          eyebrow="The Experience"
          title={<>The complete AI Doctor experience.</>}
        />
        <Reveal delay={0.1} className="mt-8 max-w-2xl">
          <p className="text-[#a7a7a2] text-lg leading-relaxed">
            Six steps. You can stop after any of them — I don't chase you into a retainer you didn't ask for.
          </p>
        </Reveal>

        <div className="mt-14 border-t border-white/10">
          {PROCESS.map(([n, t, badge, d], i) => (
            <Fragment key={n}>
            <Reveal delay={i * 0.05} className="grid grid-cols-1 lg:grid-cols-[90px_270px_1fr] gap-3 lg:gap-10 py-8 border-b border-white/10">
              <span className="font-mono text-2xl text-[#5c5c58]">{n}</span>
              <div>
                <h3 className="display text-2xl text-[#f5f3ee]">{t}</h3>
                <span className="mt-3 inline-block font-mono text-[10px] tracking-[0.18em] uppercase text-[#5CCBF5] border border-white/10 px-3 py-1.5">{badge}</span>
              </div>
              <p className="text-[#a7a7a2] text-lg leading-relaxed">{d}</p>
            </Reveal>
            {i === 0 && (
              <Reveal delay={0.1} className="my-8">
                <div className="border border-[#5CCBF5]/30 bg-[#131C31] p-8 lg:p-10" data-testid="come-prepared">
                  <p className="eyebrow mb-4">Before Your Check-Up</p>
                  <h3 className="display text-2xl sm:text-3xl text-[#f5f3ee] max-w-2xl">
                    Bring your actual day. Not the highlight reel.
                  </h3>
                  <p className="mt-6 text-[#a7a7a2] text-lg leading-relaxed max-w-3xl">
                    The Check-Up only finds what you tell me about. In the days before your session, jot it down
                    (a note on your phone is fine — nothing fancy):
                  </p>
                  <ul className="mt-6 space-y-3 max-w-3xl">
                    {[
                      "What a normal day looks like — hour by hour if you can",
                      "Your numbers, if possible — leads coming in, how fast they get answered, quotes sent",
                      "What you do every week that feels repetitive or annoying",
                      "What you do once a month that always eats more time than it should",
                      "Anything you've said “I really should automate this” about",
                      "Every tool, app or spreadsheet you currently pay for",
                    ].map((x) => (
                      <li key={x} className="flex gap-4 text-[#f5f3ee] text-lg border-b border-white/5 pb-3">
                        <span className="text-[#5CCBF5]">+</span>{x}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-6 text-[#a7a7a2] text-lg leading-relaxed max-w-3xl">
                    You don't need to organize it or make it sound impressive. The messier and more honest, the more
                    I'll find — and the less likely I am to miss something quietly costing you money.
                  </p>
                  <p className="mt-6 text-[#f5f3ee] text-lg">
                    Patients who show up with notes leave with better prescriptions.
                  </p>
                </div>
              </Reveal>
            )}
            </Fragment>
          ))}
        </div>

        <Reveal delay={0.15} className="mt-10">
          <CtaButton to="/apply" testid="process-cta">Get Your Free AI Check-Up</CtaButton>
        </Reveal>
      </section>

      {/* THE DIAGNOSIS PREVIEW */}
      <section className="relative border-y border-white/10 overflow-hidden">
        <img src="/images/ai-scan.png" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-[0.72]" />
        <div className="absolute inset-0 bg-[#0E1526]/20" />
        <div className="shell relative py-28 lg:py-40">
          <SectionHead
            eyebrow="The Diagnosis"
            title={<>This is what <span className="text-[#5CCBF5]">you walk away with.</span></>}
          />
          <Reveal delay={0.12} className="mt-14 max-w-4xl">
            <div className="border border-white/10 bg-[#0E1526]/90 backdrop-blur" data-testid="diagnosis-preview">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between border-b border-white/10 px-6 lg:px-8 py-4">
                <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#5CCBF5]">AI Business Diagnosis</span>
                <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#5c5c58]">Patient: a 4-person service business</span>
              </div>

              <div className="grid sm:grid-cols-3 gap-px bg-white/10 border-b border-white/10">
                <div className="bg-[#0E1526] px-6 lg:px-8 py-8">
                  <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#a7a7a2]">AI Health</p>
                  <p className="display mt-3 text-5xl text-[#f5f3ee] tabular-nums">64<span className="text-[#5c5c58] text-2xl"> / 100</span></p>
                </div>
                <div className="bg-[#0E1526] px-6 lg:px-8 py-8">
                  <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#a7a7a2]">AI Opportunity</p>
                  <p className="display mt-3 text-5xl text-[#f5f3ee] tabular-nums">82<span className="text-[#5c5c58] text-2xl"> / 100</span></p>
                </div>
                <div className="bg-[#0E1526] px-6 lg:px-8 py-8">
                  <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#a7a7a2]">Estimated Annual Opportunity</p>
                  <p className="display mt-3 text-4xl lg:text-5xl text-[#5CCBF5] tabular-nums">€87K–€142K</p>
                </div>
              </div>

              <div className="px-6 lg:px-8 py-6 border-b border-white/10">
                <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#a7a7a2] mb-4">Top Findings — what · why · how much · priority</p>
                {DIAGNOSIS_FINDINGS.map(([n, t, impact, conf]) => (
                  <div key={n} className="grid sm:grid-cols-[48px_1fr_auto_auto] gap-2 sm:gap-6 items-baseline py-4 border-b border-white/5 last:border-b-0">
                    <span className="font-mono text-sm text-[#5c5c58]">{n}</span>
                    <span className="display text-xl text-[#f5f3ee]">{t}</span>
                    <span className="font-mono text-[13px] text-[#5CCBF5]">{impact}</span>
                    <span className="font-mono text-[10px] tracking-[0.14em] uppercase text-[#a7a7a2] border border-white/10 px-2.5 py-1">Confidence: {conf}</span>
                  </div>
                ))}
              </div>

              <div className="px-6 lg:px-8 py-6 border-b border-white/10">
                <div className="flex flex-wrap items-baseline justify-between gap-2 mb-5">
                  <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#a7a7a2]">Time Recovery Estimate — hours returned per week</p>
                  <p className="font-mono text-[13px] text-[#5CCBF5]">Total: 8–14 hrs / week</p>
                </div>
                {[
                  ["Customer communication", "3–5 h", 92, "High"],
                  ["Lead follow-up & quotes", "2–4 h", 64, "Medium"],
                  ["Admin & reporting", "2–3 h", 52, "High"],
                  ["Scheduling & reminders", "1–2 h", 34, "High"],
                ].map(([label, range, width, conf]) => (
                  <div key={label} className="grid sm:grid-cols-[220px_1fr_auto_auto] gap-2 sm:gap-5 items-center py-3">
                    <span className="text-[#f5f3ee] text-[15px]">{label}</span>
                    <div className="h-1.5 bg-white/10 overflow-hidden">
                      <div className="h-full bg-[#5CCBF5]" style={{ width: `${width}%` }} />
                    </div>
                    <span className="font-mono text-[13px] text-[#f5f3ee] tabular-nums sm:text-right">{range} /wk</span>
                    <span className="font-mono text-[10px] tracking-[0.14em] uppercase text-[#a7a7a2]">{conf}</span>
                  </div>
                ))}
                <p className="mt-4 font-mono text-[11px] tracking-[0.1em] uppercase text-[#5c5c58]">
                  ≈ 400–700 hours per year returned to the team — estimated, based on the information provided
                </p>
              </div>

              <div className="px-6 lg:px-8 py-6 flex flex-wrap items-center gap-3">
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#a7a7a2] mr-2">Your AI Prescription</span>
                {["01 AI Front Office", "02 Quote Recovery", "03 Customer Reactivation"].map((t) => (
                  <span key={t} className="font-mono text-[11px] tracking-[0.14em] uppercase text-[#f5f3ee] border border-[#5CCBF5]/40 px-4 py-2">{t}</span>
                ))}
              </div>
            </div>
            <p className="mt-5 text-sm text-[#5c5c58] leading-relaxed">
              Estimated opportunity, based on the information provided — conservative to upside range.
              Illustrative example, not your diagnosis (yet).
            </p>
          </Reveal>
          <Reveal delay={0.18} className="mt-10">
            <CtaButton to="/apply" testid="diagnosis-cta">Get Your Free AI Check-Up</CtaButton>
          </Reveal>
        </div>
      </section>

      {/* QUICK-WIN SYSTEMS */}
      <section className="relative overflow-hidden border-b border-white/10" data-testid="quick-wins">
        <img src="/images/ai-scan.png" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-[0.72]" />
        <div className="absolute inset-0 bg-[#0E1526]/20" />
        <div className="shell relative py-28 lg:py-40">
          <SectionHead
            eyebrow="Quick-Win Systems"
            title={<>Small systems. Installed in days. <span className="text-[#a7a7a2]">Paying for themselves in weeks.</span></>}
          />
          <Reveal delay={0.1} className="mt-8 max-w-2xl">
            <p className="text-[#a7a7a2] text-lg leading-relaxed">
              These are the fixes we install most often. Your free check-up tells you which one would pay off first for your business.
            </p>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {SYSTEMS.map((s, i) => (
              <Reveal key={s.name} delay={i * 0.05} className="h-full">
                <div data-testid={`system-card-${i}`} className="group h-full bg-[#18223B] border border-white/10 p-8 flex flex-col hover:bg-[#1F2B47] hover:border-[#5CCBF5]/40 transition-colors duration-500">
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[#5CCBF5] border border-white/10 px-2.5 py-1 group-hover:border-[#5CCBF5]/40 transition-colors duration-300">System</span>
                    <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.08em] uppercase text-[#5c5c58]">
                      <Clock size={12} /> {s.time}
                    </span>
                  </div>
                  <div className="mt-7 w-14 h-14 rounded-full border border-[#5CCBF5]/30 bg-[#131C31] flex items-center justify-center text-[#5CCBF5]">
                    <s.icon size={22} />
                  </div>
                  <h3 className="display text-2xl text-[#f5f3ee] mt-6">{s.name}</h3>
                  <p className="text-[#a7a7a2] mt-3 leading-relaxed text-[15px]">{s.fixes}</p>
                  <div className="mt-6 border-y border-white/10 py-5">
                    <p className="display text-3xl text-[#5CCBF5]">{s.number}</p>
                    <p className="text-[#a7a7a2] text-sm mt-2 leading-relaxed">{s.caption}</p>
                    <p className="text-[#5c5c58] text-xs italic mt-2">{s.source}</p>
                  </div>
                  <ul className="mt-6 space-y-2.5">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex gap-3 text-[#a7a7a2] text-[15px]">
                        <Check size={16} className="text-[#5CCBF5] shrink-0 mt-0.5" />{b}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-auto pt-8">
                    <CtaButton to="/apply" variant="ghost" testid={`system-cta-${i}`} className="!px-5 !py-3">Check if it fits me</CtaButton>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1} className="mt-16">
            <div className="border border-[#5CCBF5]/30 bg-[#0E1526] p-8 lg:p-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
              <div>
                <h3 className="display text-2xl sm:text-3xl text-[#f5f3ee]">These are just the quick wins.</h3>
                <p className="text-[#a7a7a2] text-lg leading-relaxed mt-3 max-w-2xl">
                  Your free check-up looks at your whole business and shows every place AI could save you time or bring in more revenue.
                </p>
              </div>
              <CtaButton to="/apply" testid="full-system-cta" className="shrink-0">Start my free check-up</CtaButton>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="mt-8">
            <p className="text-center text-xs text-[#5c5c58] leading-relaxed max-w-2xl mx-auto">
              Examples are illustrations, not a guarantee — your numbers depend on your business.
            </p>
          </Reveal>
        </div>
      </section>

      {/* THE CORE IDEA */}
      <section className="border-b border-white/10">
        <div className="shell py-28 lg:py-40">
          <SectionHead eyebrow="The Philosophy" title={<>Most businesses don't have an AI problem. <span className="text-[#5CCBF5]">They have a clarity problem.</span></>} />
          <Reveal delay={0.1} className="mt-10 max-w-2xl">
            <p className="text-[#a7a7a2] text-lg sm:text-xl leading-relaxed">
              That's why nothing here starts with a tool. It starts with a free Check-Up — an honest examination
              of where your time, money and leads are actually going. Then a diagnosis in plain English. Then,
              only if the numbers justify it, treatment. And if the right answer is a tool you already own —
              or doing nothing — that's the answer you get.
            </p>
          </Reveal>

          <div className="mt-16 border-t border-white/10">
            {CLASSIFY.map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.05} className="group grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-4 sm:gap-10 py-8 border-b border-white/10 items-baseline">
                <span className="font-mono text-sm tracking-[0.2em] text-[#f5f3ee] group-hover:text-[#5CCBF5] transition-colors duration-300">{t}</span>
                <span className="text-[#a7a7a2] text-lg">{d}</span>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1} className="mt-14 max-w-3xl">
            <p className="display text-2xl sm:text-3xl text-[#f5f3ee] leading-tight">
              The goal isn't to replace you.
              <span className="text-[#a7a7a2]"> It's to stop you from doing $20/hour work at your best hours.</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* WHAT WE LOOK FOR */}
      <section className="relative overflow-hidden shell py-28 lg:py-40">
        <SectionBg src="/images/dna-strand.png" opacity="opacity-[0.55]" overlay="bg-[#0E1526]/35" />
          <SectionHead
            eyebrow="What I Check"
            title={<>I don't look for AI. <span className="text-[#5CCBF5]">I look for what's costing you.</span></>}
          />
          <Reveal delay={0.1} className="mt-8 max-w-2xl">
            <p className="text-[#d9d7d1] text-lg leading-relaxed">
              AI is simply one possible treatment. Sometimes the answer is a tool you already pay for.
              Sometimes it's "stop doing that entirely." The objective is the same every time:
              find economically meaningful opportunities.
            </p>
          </Reveal>
          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-white/10">
            {WECHECK.map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.04} className="group border-r border-b border-white/10 p-8 lg:p-10 hover:bg-[#18223B] transition-colors duration-500">
                <h3 className="font-mono text-[12px] tracking-[0.2em] uppercase text-[#f5f3ee] group-hover:text-[#5CCBF5] transition-colors duration-300">{t}</h3>
                <p className="text-[#d9d7d1] mt-4 leading-relaxed">{d}</p>
              </Reveal>
            ))}
          </div>
      </section>

      {/* WHAT A CHECKUP MIGHT REVEAL */}
      <section className="shell py-28 lg:py-40">
        <SectionHead
          eyebrow="What A Check-Up Might Reveal"
          title={<>Three businesses. <span className="text-[#a7a7a2]">Three hidden costs.</span></>}
        />
        <div className="mt-14 grid lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {[
            ["A solo massage therapist, fully booked most weeks.", "The No-Shows", "No automated reminder system — every confirmation sent by hand, some missed entirely.", "A text-reminder setup, self-installable in an afternoon."],
            ["A 4-person marketing agency, always slammed.", "The Inbox", "The same 5 client questions answered manually, every day, by 3 different people.", "A shared FAQ system that answers instantly — no more duplicate replies."],
            ["A local contractor, one-man operation.", "The Solo Act", "Every price quote, policy and client history lives in his head — nowhere else.", "A simple knowledge system his part-time assistant could actually use without calling him."],
          ].map(([who, symptom, reveal, fix], i) => (
            <Reveal key={i} delay={i * 0.06} className="bg-[#0E1526] p-8 lg:p-10 flex flex-col">
              <p className="text-[#f5f3ee] text-lg leading-snug">{who}</p>
              <span className="mt-5 inline-block self-start font-mono text-[10px] tracking-[0.18em] uppercase text-[#5CCBF5] border border-white/10 px-3 py-1.5">Symptom: {symptom}</span>
              <p className="mt-6 text-[#a7a7a2] leading-relaxed"><span className="text-[#f5f3ee]">What a check-up might reveal:</span> {reveal}</p>
              <p className="mt-4 text-[#a7a7a2] leading-relaxed"><span className="text-[#5CCBF5]">Possible fix:</span> {fix}</p>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1} className="mt-6">
          <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-[#e0be8e]">Illustrative examples — every check-up is different</span>
        </Reveal>
        <Reveal delay={0.15} className="mt-10">
          <CtaButton to="/apply" testid="scenarios-cta">Get Your Free AI Check-Up</CtaButton>
        </Reveal>
      </section>

      {/* BEFORE / AFTER SLIDER */}
      <section className="relative overflow-hidden shell py-28 lg:py-40">
        <SectionBg src="/images/vitals-monitor.png" opacity="opacity-[0.5]" overlay="bg-[#0E1526]/45" />
        <SectionHead eyebrow="Before & After" title="Same business. Way less babysitting." />
        <Reveal delay={0.1} className="mt-6 max-w-2xl">
          <p className="text-[#a7a7a2] text-lg leading-relaxed">
            Drag to see what a day actually looks like before and after. The goal isn't less involvement —
            it's less unnecessary involvement.
          </p>
        </Reveal>
        <Reveal delay={0.15} className="mt-12">
          <BeforeAfterSlider />
        </Reveal>
      </section>

      {/* ECONOMICS */}
      <section className="shell py-28 lg:py-40">
        <SectionHead
          eyebrow="What Your Time Is Actually Worth"
          title={<>Your most expensive employee <span className="text-[#a7a7a2]">might be you, doing $20/hr work.</span></>}
        />
        <Reveal delay={0.1} className="mt-8 max-w-2xl">
          <p className="text-[#a7a7a2] text-lg leading-relaxed">
            If your time is worth $75–$300/hr doing the work only you can do, every hour spent on repetitive admin is
            money leaking out sideways.
          </p>
        </Reveal>
        <Reveal delay={0.15} className="mt-16">
          <CapacityCalculator />
        </Reveal>
        <Reveal delay={0.1} className="mt-14 max-w-3xl">
          <p className="display text-2xl sm:text-3xl text-[#f5f3ee] leading-tight" data-testid="economics-quote">
            A couple hours back a week is a bigger deal than it sounds.
          </p>
        </Reveal>
      </section>

      {/* THE PRESCRIPTION */}
      <section className="relative overflow-hidden border-t border-white/10" data-testid="prescription-section">
        <SectionBg src="/images/diagnostics-wall.png" />
        <div className="relative shell py-28 lg:py-40">
          <SectionHead eyebrow="The Prescription" title={<>I don't sell you a pile of apps. <span className="text-[#5CCBF5]">I prescribe what your business needs.</span></>} />
          <Reveal delay={0.1} className="mt-8 max-w-2xl">
            <p className="text-[#a7a7a2] text-lg leading-relaxed">
              Six treatment categories, prescribed only after your diagnosis — each one aimed at an exact
              bottleneck in your business, never at a trend.
            </p>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {BUILDS.map((b, i) => (
              <Reveal key={b.id} delay={i * 0.06} className={b.isHero ? "md:col-span-2 md:row-span-2" : ""}>
                <div data-testid={`prescription-item-${i}`} className="group h-full bg-[#0E1526] border border-white/10 p-8 lg:p-10 flex flex-col transition-colors duration-500 hover:bg-[#18223B] hover:border-[#5CCBF5]/40">
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <span className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.18em] uppercase text-[#5CCBF5] border border-white/10 px-2.5 py-1 group-hover:border-[#5CCBF5]/40 transition-colors duration-300">
                      <span className="w-2 h-2 rounded-full bg-[#5CCBF5] animate-pulse" />
                      {b.code}
                    </span>
                    <span className="font-mono text-[10px] tracking-[0.14em] uppercase text-[#5c5c58] group-hover:text-[#5CCBF5] transition-colors duration-300">{b.efficacy}</span>
                  </div>
                  <h3 className={`display text-[#f5f3ee] mt-6 ${b.isHero ? "text-2xl lg:text-3xl" : "text-xl lg:text-2xl"}`}>{b.title}</h3>
                  <p className="text-[#a7a7a2] mt-4 leading-relaxed">{b.desc}</p>
                  <p className="mt-6 font-mono text-[11px] tracking-[0.12em] uppercase text-[#a7a7a2]">
                    <span className="text-[#5c5c58]">Active ingredient · </span>{b.ingredient}
                  </p>
                  {b.bullets && (
                    <ul className="mt-6 space-y-2.5">
                      {b.bullets.map((x) => (
                        <li key={x} className="flex gap-3 text-[#a7a7a2] text-[15px]">
                          <span className="text-[#5CCBF5]">+</span>{x}
                        </li>
                      ))}
                    </ul>
                  )}
                  {b.isHero && (
                    <div className="mt-auto pt-10">
                      <div className="border-t border-white/10 pt-5 flex items-center justify-between gap-4 flex-wrap">
                        <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[#5c5c58]">Prescribed only after diagnosis · Never before</span>
                        <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[#5CCBF5]">Most prescribed</span>
                      </div>
                    </div>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* THE CALL */}
      <section className="shell py-28 lg:py-40" data-testid="the-call">
        <SectionHead
          eyebrow="The Call"
          title={<>Your business has been checked. <span className="text-[#a7a7a2]">Now let's talk about what we found.</span></>}
        />
        <Reveal delay={0.1} className="mt-8 max-w-2xl">
          <p className="text-[#a7a7a2] text-lg leading-relaxed">
            Once you complete your free AI Business Check-Up, you'll receive your personalized AI Business
            Diagnosis — and a free call with me to walk through it together. No generic AI pitch. No sales
            presentation. Just your business, the findings, and what I think is actually worth fixing.
          </p>
        </Reveal>
        <Reveal delay={0.1} className="mt-14">
          <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#5CCBF5]">On the call, we'll go through:</p>
        </Reveal>
        <div className="mt-6 border-t border-white/10">
          {[
            ["01", "YOUR HEALTH SCORE", "Where your business currently stands across AI, automation, systems, and operational efficiency."],
            ["02", "YOUR BIGGEST OPPORTUNITIES", "The specific places where you may be losing time, money, leads, or capacity — backed by the information you gave me."],
            ["03", "THE NUMBERS", "We'll look at the potential annual value of the biggest opportunities — what I know, what I'm estimating, and where more information is needed."],
            ["04", "YOUR AI PRESCRIPTION", "The 3–5 treatments I believe could make the biggest difference to your business — prioritized by impact, not by what happens to be easiest to sell."],
            ["05", "WHAT TO DO NEXT", "We'll decide what, if anything, is actually worth implementing. Sometimes that means working with me. Sometimes it means using a tool you already have. Anyways, let's talk about it."],
          ].map(([n, t, d], i) => (
            <Reveal key={n} delay={i * 0.05} className="grid grid-cols-1 lg:grid-cols-[90px_320px_1fr] gap-3 lg:gap-10 py-8 border-b border-white/10">
              <span className="font-mono text-2xl text-[#5c5c58]">{n}</span>
              <h3 className="display text-2xl text-[#f5f3ee]">{t}</h3>
              <p className="text-[#a7a7a2] text-lg leading-relaxed">{d}</p>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1} className="mt-14 max-w-3xl">
          <p className="eyebrow mb-4">The Goal</p>
          <p className="display text-2xl sm:text-3xl text-[#f5f3ee] leading-tight">
            You leave the call knowing exactly where your business stands, what's costing you, and what I would do about it.
          </p>
        </Reveal>
        <Reveal delay={0.15} className="mt-10">
          <p className="text-[#a7a7a2] text-lg leading-relaxed max-w-2xl">
            Once your check-up is complete, let's talk about it — pick a time that suits you and we'll walk
            through your diagnosis together.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <CtaButton to="/apply" testid="the-call-cta">Get Your Free AI Check-Up</CtaButton>
            <CtaButton to="https://calendar.zoho.eu/zc/view/slot-booking/0801123e391a7490dcb254a0c4b03d9500005beceaaf3273253bcea258d7c19b7f05e2fca8b4029aa9526476cc25dd261117c256b5c6e7ceb6a83e0d2ca6a8802f08" variant="ghost" testid="book-call-cta">Book Your Free Call</CtaButton>
          </div>
        </Reveal>
      </section>

      {/* WHY AI DOCTOR */}
      <section className="relative overflow-hidden shell py-28 lg:py-40">
        <SectionBg src="/images/doctor-portrait.png" opacity="opacity-[0.38]" overlay="bg-[#0E1526]/55" />
        <div className="max-w-3xl mx-auto text-center" data-testid="why-doctor-image">
        <SectionHead
          className="[&_h2]:mx-auto"
          eyebrow="Why AI Doctor"
          title={<>Everyone else starts with what they sell. <span className="text-[#a7a7a2]">I start with what's wrong.</span></>}
        />
        <div className="mt-12 border-t border-white/10">
          {[
            ["Traditional AI agencies", "“Tell us what you want automated.”", "I examine your business and tell you what should be automated."],
            ["Traditional software", "“Here are our features.”", "Here is what your business needs."],
            ["Traditional consultants", "“Here is our strategy.”", "Here is your diagnosis, prescription and treatment plan."],
          ].map(([who, them, us], i) => (
            <Reveal key={who} delay={i * 0.05} className="grid gap-5 py-8 border-b border-white/10">
              <div>
                <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#5c5c58]">{who}</p>
                <p className="mt-3 text-[#a7a7a2] text-xl leading-relaxed line-through decoration-white/20">{them}</p>
              </div>
              <div>
                <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#5CCBF5]">AI Doctor</p>
                <p className="mt-3 display text-2xl text-[#f5f3ee] leading-snug">“{us}”</p>
              </div>
            </Reveal>
          ))}
        </div>
        </div>
      </section>

      {/* WHAT STAYS WITH FOUNDER */}
      <section className="shell py-28 lg:py-40">
        <SectionHead eyebrow="Human + AI" title={<>I don't remove you. <span className="text-[#a7a7a2]">I remove the wrong work.</span></>} />
        <div className="mt-16 grid lg:grid-cols-2 gap-px bg-white/10 border border-white/10">
          <div className="bg-[#0E1526] p-8 lg:p-12">
            <p className="eyebrow mb-8">You Should Keep</p>
            <ul className="space-y-4">
              {["The actual craft", "Real relationships", "The big decisions", "Your voice and taste", "Pricing and strategy", "The parts you enjoy"].map((x) => (
                <li key={x} className="text-[#f5f3ee] text-lg sm:text-xl border-b border-white/5 pb-4">{x}</li>
              ))}
            </ul>
          </div>
          <div className="bg-[#131C31] p-8 lg:p-12">
            <p className="eyebrow-muted mb-8">The System Handles</p>
            <ul className="space-y-4">
              {["Repetitive replies", "Appointment reminders", "Lead follow-ups", "First drafts", "Scheduling and invoicing", "FAQ answers", "Data entry", "Chasing no-shows"].map((x) => (
                <li key={x} className="text-[#a7a7a2] text-lg border-b border-white/5 pb-4">{x}</li>
              ))}
            </ul>
          </div>
        </div>
        <Reveal delay={0.1} className="mt-12 max-w-3xl">
          <p className="display text-2xl sm:text-3xl text-[#f5f3ee] leading-tight">
            Your business should run on your judgment —<span className="text-[#a7a7a2]"> not your availability at 11pm.</span>
          </p>
        </Reveal>
      </section>

      {/* TREATMENT — DIAGNOSIS IS ONLY THE BEGINNING */}
      <section className="relative overflow-hidden shell py-28 lg:py-40">
        <SectionBg src="/images/corridor.png" />
        <SectionHead
          eyebrow="The Treatment"
          title={<>Diagnosis is only the beginning. <span className="text-[#a7a7a2]">I don't just tell you what's wrong — I treat it.</span></>}
        />
        <Reveal delay={0.1} className="mt-8 max-w-2xl">
          <p className="text-[#a7a7a2] text-lg leading-relaxed">
            Treatment can look very different from one business to the next. These are the three shapes it takes —
            never a menu to browse, always a consequence of your diagnosis.
          </p>
        </Reveal>
        <div className="mt-14 grid lg:grid-cols-3 gap-px bg-white/10 border border-white/10" data-testid="treatment-types">
          {[
            ["01", "Quick Fixes", "Simple AI Treatments", "Fast, focused treatments for clear problems that can be solved with a relatively simple intervention.", ["Missed-call recovery", "Instant lead response", "Review requests", "Appointment reminders", "Lead qualification"]],
            ["02", "Systems", "Connected AI Systems", "When the opportunity requires several tools, workflows or processes working together around one business outcome.", ["AI Front Office", "Quote follow-up", "Customer reactivation", "AI customer service"]],
            ["03", "Structural Work", "Custom Automation", "When the problem isn't a tool. I redesign the workflow around the way the business actually operates.", ["Complex operational workflows", "Internal knowledge systems", "Multi-step automation", "Custom AI systems", "Process redesign"]],
          ].map(([n, name, sub, d, examples]) => (
            <div key={name} className="bg-[#0E1526] p-8 lg:p-10 flex flex-col">
              <p className="font-mono text-2xl text-[#5c5c58]">{n}</p>
              <h3 className="display text-2xl text-[#f5f3ee] mt-5 uppercase tracking-wide">{name}</h3>
              <p className="font-mono text-[11px] tracking-[0.14em] uppercase text-[#5CCBF5] mt-2">{sub}</p>
              <p className="text-[#a7a7a2] mt-4 leading-relaxed">{d}</p>
              <ul className="mt-6 space-y-2.5">
                {examples.map((x) => (
                  <li key={x} className="flex gap-3 text-[#a7a7a2] text-[15px]">
                    <span className="text-[#5CCBF5]">+</span>{x}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <Reveal delay={0.1} className="mt-14 max-w-3xl">
          <p className="display text-2xl sm:text-3xl text-[#f5f3ee] leading-tight">
            The treatment depends on the diagnosis.
          </p>
          <p className="mt-4 text-[#a7a7a2] text-lg leading-relaxed">
            Your prescription is personalized to your business — its revenue, its people, its systems.
            I don't recommend a treatment until I understand the problem. That's why there are no prices on this
            page: quoting before diagnosing would be malpractice.
          </p>
        </Reveal>
        <Reveal delay={0.15} className="mt-10 flex flex-wrap gap-4">
          <CtaButton to="/apply" testid="treatment-cta">Get Your Free AI Check-Up</CtaButton>
        </Reveal>
      </section>

      {/* AI CARE */}
      <section className="border-y border-white/10">
        <div className="shell py-28 lg:py-40 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <SectionHead eyebrow="After Treatment" title={<>AI Care. <span className="text-[#a7a7a2]">I don't disappear after the prescription.</span></>} />
          </div>
          <Reveal delay={0.1}>
            <p className="text-[#a7a7a2] text-lg leading-relaxed">
              Diagnosis identifies the opportunities. Treatment implements the systems. AI Care keeps them healthy:
              monitoring, optimization, new automations, reporting, ongoing support and regular system health checks.
              Your business changes — your AI should keep up.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {["Monitoring", "Optimization", "New automations", "Reporting", "Ongoing support", "Health checks"].map((x) => (
                <span key={x} className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#a7a7a2] border border-white/10 px-4 py-2">{x}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHO FOR / NOT FOR */}
      <section className="relative overflow-hidden shell py-28 lg:py-40">
        <SectionBg src="/images/clinic-reception.png" opacity="opacity-[0.45]" overlay="bg-[#0E1526]/45" />
        <SectionHead
          eyebrow="Who I See"
          title={<>You don't need to be a $10M company. <span className="text-[#a7a7a2]">You need five hours a week back.</span></>}
        />
        <Reveal delay={0.08} className="mt-6 max-w-2xl">
          <p className="text-[#a7a7a2] text-lg leading-relaxed">
            I work across professional services, home services, healthcare, agencies, ecommerce, construction,
            hospitality and more. The symptoms are the same everywhere — the Check-Up adapts to your industry.
          </p>
        </Reveal>
        <div className="mt-16 grid lg:grid-cols-2 gap-12 lg:gap-20">
          <Reveal>
            <p className="eyebrow mb-8">This is for</p>
            <ul className="space-y-4">
              {["Solo operators & freelancers", "Small teams (1–15 people)", "Coaches, consultants, wellness practitioners", "Local service businesses (salons, clinics, contractors, restaurants…)", "Anyone tired of “figuring out AI” alone at 11pm", "People who'd rather ask a professional than watch 40 YouTube videos"].map((x) => (
                <li key={x} className="flex gap-4 text-[#f5f3ee] text-lg border-b border-white/5 pb-4">
                  <span className="text-[#5CCBF5]">✓</span>{x}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="eyebrow-muted mb-8">This is not for</p>
            <ul className="space-y-4">
              {["People who aren't ready to change how they work", "Companies resistant to new technology — no diagnosis can fix that", "Anyone after a magic button — the tools work if you do", "Enterprises needing a 6-month transformation program (I'll point you elsewhere)"].map((x) => (
                <li key={x} className="flex gap-4 text-[#a7a7a2] text-lg border-b border-white/5 pb-4">
                  <span className="text-[#5c5c58]">×</span>{x}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
        <Reveal delay={0.1} className="mt-12 max-w-3xl">
          <p className="text-[#f5f3ee] text-lg sm:text-xl leading-relaxed">
            I'm not here to add more software to your stack.
            <span className="text-[#a7a7a2]"> I'm here to tell you which 3 pieces actually matter.</span>
          </p>
        </Reveal>
      </section>

      {/* PHILOSOPHY — full width statement */}
      <section className="border-y border-white/10">
        <div className="shell py-32 lg:py-48">
          <Reveal>
            <p className="display text-3xl sm:text-5xl lg:text-6xl text-white leading-[1.05] max-w-4xl">
              Your business doesn't need more AI.
              <span className="text-[#5CCBF5]"> It needs the right AI.</span>
            </p>
          </Reveal>
          <Reveal delay={0.15} className="mt-12 max-w-xl">
            <p className="text-[#d9d7d1] text-lg leading-relaxed">
              Three tools you use beat forty you bookmarked. I'd rather prescribe less and have it work than hand you
              a homework list you'll never finish.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-white/10 bg-[#131C31]">
        <div className="shell py-28 lg:py-40 grid lg:grid-cols-[0.8fr_1.2fr] gap-14">
          <SectionHead eyebrow="Questions" title="Before you book." className="lg:sticky lg:top-28 lg:self-start" />
          <Reveal delay={0.1}>
            <Accordion items={FAQ} testid="home-faq" />
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section ref={ctaRef} className="relative overflow-hidden">
        <motion.img
          src="/images/clinic-atrium.png"
          alt=""
          aria-hidden="true"
          style={{ y: imgY }}
          className="absolute inset-0 h-[120%] w-full object-cover opacity-[0.72]"
        />
        <div className="absolute inset-0 bg-[#0E1526]/40" />
        <div className="shell relative py-32 lg:py-52 text-center flex flex-col items-center">
        <Reveal>
          <h2 className="display text-[#f5f3ee] text-4xl sm:text-6xl lg:text-7xl max-w-5xl mx-auto">
            Your business has symptoms. <span className="text-[#5CCBF5]">Let's find the cause.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.12} className="mt-10 max-w-xl">
          <p className="text-[#d9d7d1] text-lg leading-relaxed">
            One free examination. A clear diagnosis. Zero pressure to buy anything else.
          </p>
        </Reveal>
        <Reveal delay={0.2} className="mt-12 flex flex-wrap gap-4 justify-center">
          <CtaButton to="/apply" testid="final-primary-cta">Get Your Free AI Check-Up</CtaButton>
        </Reveal>
        <Reveal delay={0.28} className="mt-8">
          <p className="font-mono text-[11px] tracking-[0.14em] uppercase text-[#5c5c58]">Free. 30–45 minutes. No card required.</p>
        </Reveal>
        </div>
      </section>
    </div>
  );
}
